import { ReactNode, useMemo, useState } from 'react';
import { allowOrWarn, Util } from '../util/Util';
import { useAuth } from '../hooks/useAuth';
import { useUI } from '../providers/MoleUIContext';
import { useDailyAggregateData } from '../hooks/useDailyAggregateData';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from '../util/constants';
import TableView from './TableView';
import type { DataScopeResource } from '../hooks/useDataScope';
import { MRT_ColumnDef, MRT_TableInstance } from 'material-react-table';
import { Box, IconButton, Tooltip } from '@mui/material';
import ExportMenuButton from './ExportMenuButton';
import { AddCircleOutline, Refresh } from '@mui/icons-material';
import { createSignCell } from '../util/cellFormatters';
import PaymentModal from '../features/payments/PaymentModal';
import SupplierBulkPaymentModal from '../features/payments/SupplierBulkPaymentModal';
import { toast } from 'react-toastify';
import { parseISODay } from "../util/businessClock";

import { roundQty } from "../util/quantity";
import { canRecord } from '../util/access';
export type DailyAggregateExtraColumn = {
	accessorKey: string;
	headerKey: string;
};

export type DailyAggregateConfig = {
	storageKey: string;
	headerKey: string;
	endpoint: string;
	exportPrefix: string;
	dimensionKey: string;
	dimensionHeaderKey: string;
	extraColumns?: DailyAggregateExtraColumn[];
	highlightRefunds?: boolean;
	enablePaymentCreate?: boolean;
	enableSupplyPaymentCreate?: boolean;
	/** Record type whose read scope governs this summary; inferred from `endpoint` when omitted. */
	dataScope?: DataScopeResource;
};

/** Summary endpoints are scoped like the list of the same record type. */
export const inferDailyAggregateScope = (endpoint: string): DataScopeResource | undefined => {
	const path = endpoint.replace(/^\/+/, '');
	if (path.startsWith('supplier-payments/')) return 'supply_payment';
	if (path.startsWith('payments/')) return 'payment';
	if (path.startsWith('sales/')) return 'sale';
	return undefined;
};

export const AmountFooter = ({
	table,
	label,
	accessorKey = 'amount',
}: {
	table: MRT_TableInstance<any>;
	label: string;
	accessorKey?: string;
}) => {
	const total = table.getFilteredRowModel().rows.reduce((sum, row) => {
		const value = Number(row.original[accessorKey]);
		return Number.isFinite(value) ? sum + value : sum;
	}, 0);

	return (
		<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
			{label}: {Util.formatNumber(roundQty(total))}
		</Box>
	);
};

const buildAmountColumn = (
	t: (key: string) => string,
	accessorKey = 'amount',
): MRT_ColumnDef<any> => ({
	header: t(accessorKey === 'amount' ? 'common:tables.amount' : `common:tables.${accessorKey}`),
	accessorKey,
	Cell: createSignCell({ signStyle: 'accounting' }),
	filterFn: 'contains',
	columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
	aggregationFn: 'sum',
	AggregatedCell: ({ cell, table }) => (
		<>
			{t('common:tableView.sumBy')}{' '}
			{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
			<Box sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}>
				{Util.formatNumber(cell.getValue<number>())}
			</Box>
		</>
	),
	Footer: ({ table }) => (
		<AmountFooter
			table={table}
			label={t('common:report.total')}
			accessorKey={accessorKey}
		/>
	),
});

const DailyAggregateTable = ({ config }: { config: DailyAggregateConfig }) => {
	const { session, access } = useAuth();
	const { t } = useUI();
	const [newPaymentOpen, setNewPaymentOpen] = useState(false);
	const {
		data,
		lastUpdate,
		dateRange,
		setDateRange,
		loading,
		fetchData,
	} = useDailyAggregateData(config.storageKey, config.endpoint);

	const handlePaymentSuccess = () => {
		toast.success(`${t('common:toast.success.changes')} ${t('common:toast.loading.refresh')}`);
		void fetchData();
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
		const dateColumn: MRT_ColumnDef<any> = {
			header: t('common:tables.date'),
			id: 'date',
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), 'medium'),
			filterVariant: 'date-range',
		};

		const extraCols: MRT_ColumnDef<any>[] = (config.extraColumns ?? []).map((col) => ({
			header: t(col.headerKey),
			accessorKey: col.accessorKey,
			filterVariant: 'autocomplete' as const,
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		}));

		const dimensionColumn: MRT_ColumnDef<any> = {
			header: t(config.dimensionHeaderKey),
			accessorKey: config.dimensionKey,
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		};

		return [dateColumn, ...extraCols, dimensionColumn, buildAmountColumn(t)];
	}, [t, config.dimensionHeaderKey, config.dimensionKey, config.extraColumns]);

	const getRowSx = useMemo(
		() => config.highlightRefunds
			? (row: any) =>
					Util.compareStrings(row.original?.y_type || '', 'Refund')
						? {
								backgroundColor: (theme: any) =>
									theme.palette.mode === 'dark'
										? 'rgba(255, 167, 38, 0.12)'
										: 'rgba(255, 167, 38, 0.08)',
								borderLeft: '3px solid',
								borderLeftColor: 'warning.main',
							}
						: {}
			: undefined,
		[config.highlightRefunds],
	);

	const paymentCreateButton: ReactNode = config.enablePaymentCreate ? (
		<Tooltip title={t('common:ui.tooltips.newPayment')}>
			<IconButton
				onClick={() => {
					if (
						allowOrWarn(canRecord(access, 'payment'))
						|| allowOrWarn(canRecord(access, 'refund'))
					) {
						setNewPaymentOpen(true);
					}
				}}
				color="success"
			>
				<AddCircleOutline />
			</IconButton>
		</Tooltip>
	) : config.enableSupplyPaymentCreate ? (
		<Tooltip title={t('common:ui.tooltips.newPayment')}>
			<IconButton
				onClick={() => {
					if (allowOrWarn(canRecord(access, 'supply_payment'))) {
						setNewPaymentOpen(true);
					}
				}}
				color="success"
			>
				<AddCircleOutline />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={config.storageKey}
					dataScope={config.dataScope ?? inferDailyAggregateScope(config.endpoint)}
					header={t(config.headerKey)}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[{ key: 'amount', label: t('common:tables.amount') }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setDateRange}
					isLoading={loading}
					getRowSx={getRowSx}
					enableRowActions={false}
					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t('common:ui.tooltips.refreshData')}>
								<IconButton onClick={() => void fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							{paymentCreateButton}
							<ExportMenuButton
								prefix={config.exportPrefix}
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
				/>
			</LocalizationProvider>

			{config.enablePaymentCreate && newPaymentOpen && (
				<PaymentModal
					isOpen={newPaymentOpen}
					toggle={() => setNewPaymentOpen(false)}
					onSuccess={handlePaymentSuccess}
				/>
			)}

			{config.enableSupplyPaymentCreate && newPaymentOpen && (
				<SupplierBulkPaymentModal
					isOpen={newPaymentOpen}
					toggle={() => setNewPaymentOpen(false)}
					onSuccess={handlePaymentSuccess}
				/>
			)}
		</>
	);
};

export default DailyAggregateTable;